import React from 'react';
import PREFERRED_PRICE_DISPLAY from '../components/constants/preferredPriceDisplay';
import ITEM_CUSTOM_PACKING_TYPE from '../components/constants/itemCustomPackingType';
import ITEM_TEMPERATURE from '../components/constants/itemTemperature';
import ITEM_CHARACTERISTIC from '../components/constants/itemCharacteristic';
import ITEM_PROMOTIONAL_BADGE from '../components/constants/itemPromotionalBadge';
import ITEM_MEASUREMENT_UNIT from '../components/constants/itemMeasurementUnit';

const getFirstLevelCategoryOptions = (categories, intl, addEnumName) => {
  return categories
    ? categories.map((category) => {
        const translationString = `item.first.level.category.${category.name}`;
        return {
          value: category.name,
          label: intl.messages[translationString]
            ? `${intl.formatMessage({ id: translationString })}${addEnumName ? ` - ${category.name}` : ''}`
            : category.name,
        };
      })
    : [];
};

const getSecondLevelCategoryOptions = (selectedFirstLevelCategory, intl, addEnumName) => {
  return selectedFirstLevelCategory
    ? selectedFirstLevelCategory.secondLevelCategories.map((category) => {
        const translationString = `item.second.level.category.${category.name}`;
        return {
          value: category.name,
          label: intl.messages[translationString]
            ? `${intl.formatMessage({ id: translationString })}${addEnumName ? ` - ${category.name}` : ''}`
            : category.name,
        };
      })
    : [];
};

const getThirdLevelCategoryOptions = (selectedSecondLevelCategory, intl, addEnumName) => {
  return selectedSecondLevelCategory?.thirdLevelCategories.length
    ? selectedSecondLevelCategory.thirdLevelCategories.map((category) => {
        const translationString = `item.third.level.category.${category.name}`;
        return {
          value: category.name,
          label: intl.messages[translationString]
            ? `${intl.formatMessage({ id: translationString })}${addEnumName ? ` - ${category.name}` : ''}`
            : category.name,
        };
      })
    : null;
};

const getItemForm = function (item, isAdmin, marketplaces, intl, itemFirstLevelCategories) {
  const temperatureOptions = Object.keys(ITEM_TEMPERATURE).map((key) => {
    const type = ITEM_TEMPERATURE[key];
    return {
      value: type.name,
      label: intl.formatMessage({ id: 'item.form.field.temperature.name.' + type.name }),
    };
  });

  const firstMarketplace =
    item.marketplaces?.length && marketplaces.length
      ? marketplaces.find((mp) => mp.id === item.marketplaces[0].marketplaceId)
      : null;

  const itemCharacteristicOptions = Object.keys(ITEM_CHARACTERISTIC).map((key) => {
    const characteristic = ITEM_CHARACTERISTIC[key];
    return {
      value: characteristic.name,
      label: intl.formatMessage({ id: `item.characteristic.${characteristic.name}` }),
    };
  });

  const itemMeasurementUnitOptions = Object.keys(ITEM_MEASUREMENT_UNIT).map((key) => {
    const characteristic = ITEM_MEASUREMENT_UNIT[key];
    return {
      value: characteristic.name,
      label: intl.formatMessage({ id: `item.measurement.unit.${characteristic.name}` }),
    };
  });

  const itemFirstLevelCategoryOptions = getFirstLevelCategoryOptions(itemFirstLevelCategories, intl);

  const selectedFirstLevelCategory =
    item?.firstLevelCategory && itemFirstLevelCategories
      ? itemFirstLevelCategories.find((it) => it.name === item.firstLevelCategory)
      : null;

  const itemSecondLevelCategoryOptions = getSecondLevelCategoryOptions(selectedFirstLevelCategory, intl);

  const selectedSecondLevelCategory =
    item?.secondLevelCategory && selectedFirstLevelCategory
      ? selectedFirstLevelCategory.secondLevelCategories.find((it) => it.name === item.secondLevelCategory)
      : null;

  const itemThirdLevelCategoryOptions = getThirdLevelCategoryOptions(selectedSecondLevelCategory, intl);

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      infoFields: {
        title: intl.formatMessage({ id: 'item.form.info.fields.legend' }),
        type: 'object',
        required: [],
        properties: {
          name: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.name.label' }),
          },
          description: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.description.label' }),
            default: '',
          },
          ingredients: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.ingredients.label' }),
            default: '',
          },
          nutritionalInfo: {
            type: 'object',
            required: [],
            properties: {
              energyInKcal: {
                type: 'string',
                default: '',
              },
              fat: {
                type: 'string',
                default: '',
              },
              carbs: {
                type: 'string',
                default: '',
              },
              sugar: {
                type: 'string',
                default: '',
              },
              protein: {
                type: 'string',
                default: '',
              },
              salt: {
                type: 'string',
                default: '',
              },
            },
          },
          characteristics: {
            type: 'array',
            title: intl.formatMessage({ id: 'item.form.field.characteristics.label' }),
            options: itemCharacteristicOptions,
          },
          measurementUnit: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.measurement.unit.label' }),
            options: itemMeasurementUnitOptions,
          },
          standardUnitQuantity: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.standard.unit.quantity.label' }),
            default: '',
          },
          firstLevelCategory: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.first.level.category.label' }),
            options: itemFirstLevelCategoryOptions,
            showClearOption: true,
          },
          secondLevelCategory: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.second.level.category.label' }),
            options: itemSecondLevelCategoryOptions,
            showClearOption: true,
          },
          thirdLevelCategory: itemThirdLevelCategoryOptions
            ? {
                type: 'string',
                title: intl.formatMessage({ id: 'item.form.field.third.level.category.label' }),
                options: itemThirdLevelCategoryOptions,
                showClearOption: true,
              }
            : null,
          gtin: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.gtin.label' }),
            default: '',
          },
          internalId: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.internal.id.label' }),
            default: '',
          },
        },
      },
      storageFields: {
        title: intl.formatMessage({ id: 'item.form.storage.fields.legend' }),
        type: 'object',
        required: [],
        properties: {
          temperature: {
            type: 'string',
            title: intl.formatMessage({ id: 'item.form.field.temperature.label' }),
            options: temperatureOptions,
            showClearOption: true,
          },
        },
      },
      priceFields: {
        type: 'object',
        required: [],
        vatPercentage: firstMarketplace ? firstMarketplace.vatPercentage : 20,
        feePercentage: firstMarketplace?.fees.length ? firstMarketplace.fees[0].feePercentage : 35,
        properties: {
          wholesalePrice: {
            type: 'string',
            default: '',
          },
          price: {
            type: 'string',
            default: '',
          },
          unitPrice: {
            type: 'string',
            default: '',
          },
          preferredPriceDisplay: {
            type: 'string',
            default: PREFERRED_PRICE_DISPLAY.price,
          },
        },
      },
      marketplaces: {
        type: 'array',
        title: intl.formatMessage({ id: 'item.form.field.marketplaces.label' }),
        marketplaces: marketplaces,
      },
      isPrivate: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'item.form.field.is.private.label' }),
        default: false,
      },
    },
  };

  if (!formSchema.properties.infoFields.properties.thirdLevelCategory) {
    delete formSchema.properties.infoFields.properties.thirdLevelCategory;
  }

  if (isAdmin) {
    const promotionalBadgeOptions = Object.keys(ITEM_PROMOTIONAL_BADGE).map((key) => {
      const badge = ITEM_PROMOTIONAL_BADGE[key];
      return {
        value: badge.name,
        label: intl.formatMessage({ id: `item.promotional.badge.${badge.name}` }),
      };
    });

    const temperatureOptions = Object.keys(ITEM_CUSTOM_PACKING_TYPE).map((key) => {
      const type = ITEM_CUSTOM_PACKING_TYPE[key];
      return {
        value: type.name,
        label: intl.formatMessage({ id: 'item.form.field.custom.packing.type.name.' + type.name }),
      };
    });

    const showCustomPackingTypeOtherField = [
      ITEM_CUSTOM_PACKING_TYPE.otherQuantity.name,
      ITEM_CUSTOM_PACKING_TYPE.otherBoolean.name,
    ].includes(item.adminFields.customPackingType);

    const adminFields = {
      title: intl.formatMessage({ id: 'item.form.admin.fields.legend' }),
      type: 'object',
      required: [],
      properties: {
        customPackingType: {
          type: 'string',
          title: intl.formatMessage({ id: 'item.form.field.custom.packing.type.label' }),
          options: temperatureOptions,
          showClearOption: true,
        },
        promotionalBadge: {
          type: 'string',
          title: intl.formatMessage({ id: 'item.form.field.promotional.badge.label' }),
          options: promotionalBadgeOptions,
          showClearOption: true,
        },
        customPackingTypeOtherLabel: showCustomPackingTypeOtherField
          ? {
              type: 'string',
              title: intl.formatMessage({ id: 'item.form.field.custom.packing.type.other.label.label' }),
            }
          : null,
        shouldDoWeightRefunds: {
          type: 'boolean',
          title: intl.formatMessage({ id: 'item.form.field.should.do.weight.refunds.label' }),
        },
        isBusinessToConsumer: {
          type: 'boolean',
          default: true,
          title: intl.formatMessage({ id: 'item.form.field.is.business.to.consumer.label' }),
        },
        isBusinessToBusiness: {
          type: 'boolean',
          default: false,
          title: intl.formatMessage({ id: 'item.form.field.is.business.to.business.label' }),
        },
      },
    };

    if (!adminFields.properties.customPackingTypeOtherLabel) {
      delete adminFields.properties.customPackingTypeOtherLabel; // formSchema doesn't accept null values for properties
    }

    formSchema.properties.adminFields = adminFields;
    formSchema.properties.marketplaces.showAdminFields = true;
  }

  const uiSchema = {
    infoFields: {
      description: {
        'ui:field': 'richText',
      },
      ingredients: {
        'ui:field': 'richText',
        'ui:help': intl.formatMessage({ id: 'item.form.field.ingredients.help' }),
      },
      nutritionalInfo: {
        'ui:field': 'nutritionalInfoFields',
      },
      characteristics: {
        'ui:field': 'multiSelect',
      },
      measurementUnit: {
        'ui:field': 'customSelect',
      },
      firstLevelCategory: {
        'ui:field': 'customSelect',
      },
      secondLevelCategory: {
        'ui:field': 'customSelect',
      },
      thirdLevelCategory: {
        'ui:field': 'customSelect',
      },
      standardUnitQuantity: {
        'ui:help': intl.formatMessage({ id: 'item.form.field.standard.unit.quantity.help' }),
      },
      internalId: {
        'ui:help': intl.formatMessage({ id: 'item.form.field.internal.id.help' }),
      },
    },
    storageFields: {
      temperature: {
        'ui:field': 'customSelect',
      },
    },
    priceFields: {
      'ui:field': 'priceFields',
    },
    marketplaces: {
      'ui:field': 'itemMarketplaces',
    },
    isPrivate: {
      'ui:help': intl.formatMessage({ id: 'item.form.field.is.private.help' }),
    },
    adminFields: {
      customPackingType: {
        'ui:help': intl.formatMessage({ id: 'item.form.field.custom.packing.type.help' }),
        'ui:field': 'customSelect',
      },
      promotionalBadge: {
        'ui:field': 'customSelect',
      },
    },
  };

  return {
    schema: formSchema,
    uiSchema,
  };
};

const getFormObjectFromItem = (item, marketplace) => {
  if (!item) {
    return {
      infoFields: {
        characteristics: [],
      },
      adminFields: {},
      storageFields: {},
      priceFields: {},
      marketplaces: marketplace ? [{ marketplaceId: marketplace.id }] : [],
    };
  }

  return {
    ...item,
    infoFields: {
      name: item.name,
      description: item.description,
      ingredients: item.ingredients,
      nutritionalInfo: item.nutritionalInfo
        ? {
            energyInKcal: item.nutritionalInfo.energyInKcal ? `${item.nutritionalInfo.energyInKcal}` : null,
            fat: item.nutritionalInfo.fat ? `${item.nutritionalInfo.fat}` : null,
            fattyAcids: item.nutritionalInfo.fattyAcids ? `${item.nutritionalInfo.fattyAcids}` : null,
            carbs: item.nutritionalInfo.carbs ? `${item.nutritionalInfo.carbs}` : null,
            sugar: item.nutritionalInfo.sugar ? `${item.nutritionalInfo.sugar}` : null,
            protein: item.nutritionalInfo.protein ? `${item.nutritionalInfo.protein}` : null,
            salt: item.nutritionalInfo.salt ? `${item.nutritionalInfo.salt}` : null,
          }
        : {},
      characteristics: item.characteristics.map((c) => c.name),
      measurementUnit: item.measurementUnit,
      firstLevelCategory: item.firstLevelCategory,
      secondLevelCategory: item.secondLevelCategory,
      thirdLevelCategory: item.thirdLevelCategory,
      standardUnitQuantity: item.standardUnitQuantity ? `${item.standardUnitQuantity}` : '',
      gtin: item.gtin,
      internalId: item.internalId,
    },
    storageFields: {
      temperature: item.temperature,
    },
    priceFields: {
      ...item.priceFields,
      wholesalePrice: item.priceFields.wholesalePrice ? `${item.priceFields.wholesalePrice}` : '',
      price: `${item.priceFields.price}`,
      comparedToPrice: item.priceFields.comparedToPrice ? `${item.priceFields.comparedToPrice}` : '',
      wholesaleUnitPrice: item.priceFields.wholesaleUnitPrice ? `${item.priceFields.wholesaleUnitPrice}` : '',
      unitPrice: item.priceFields.unitPrice ? `${item.priceFields.unitPrice}` : '',
      groupPrice: item.priceFields.groupPrice ? `${item.priceFields.groupPrice}` : '',
    },
    marketplaces: item.marketplaces.map((mp) => {
      return {
        ...mp,
        stock: mp.stock || mp.stock === 0 ? mp.stock.toString() : null,
        stockWarning: mp.stockWarning || mp.stock === 0 ? mp.stockWarning.toString() : null,
      };
    }),
    adminFields: {
      promotionalBadge: item.promotionalBadge,
      customPackingType: item.customPackingType,
      customPackingTypeOtherLabel: item.customPackingTypeOtherLabel,
      shouldDoWeightRefunds: item.shouldDoWeightRefunds,
      isBusinessToConsumer: item.isBusinessToConsumer,
      isBusinessToBusiness: item.isBusinessToBusiness,
    },
  };
};

const getServerItemFromFormObject = (formObj) => {
  return {
    ...formObj,
    ...formObj.infoFields,
    ...formObj.storageFields,
    ...formObj.adminFields,
  };
};

const getRoutingProfiles = () => {
  return [
    {
      value: 'slower-0-7',
      label: '0.7',
    },
    {
      value: 'slower-0-5',
      label: '0.5',
    },
    {
      value: 'slower-0-3',
      label: '0.3',
    },
  ];
};

const forms = {
  getItemForm,
  getFormObjectFromItem,
  getServerItemFromFormObject,
  getFirstLevelCategoryOptions,
  getSecondLevelCategoryOptions,
  getThirdLevelCategoryOptions,
  getRoutingProfiles,
};

export default forms;
